import { Logout } from '@mui/icons-material'
import {
  Alert,
  AppBar,
  Box,
  Container,
  Drawer,
  IconButton,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Snackbar,
  Toolbar,
  Typography,
} from '@mui/material'
import type React from 'react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { useChat } from '../hooks/useChat'
import { AssistantSelector } from './AssistantSelector'
import { ChatInput } from './ChatInput'
import { ChatMessage } from './ChatMessage'
import { ProfileAvatar } from './ProfileAvatar'

export const ChatInterface: React.FC = () => {
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [selectedAssistantId, setSelectedAssistantId] = useState<string | null>(null)
  const [, setSelectedAssistantName] = useState<string>('') // Used in handleAssistantChange
  const [profileDrawerOpen, setProfileDrawerOpen] = useState(false)
  const { logout, user } = useAuth()
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const { messages, isLoading, sendMessage, clearMessages } = useChat({
    assistantId: selectedAssistantId || '',
    onError: setError,
  })

  const handleAssistantChange = (assistantId: string, assistantName: string) => {
    setSelectedAssistantId(assistantId)
    setSelectedAssistantName(assistantName)
    clearMessages()

    // Update URL with selected assistant
    const newSearchParams = new URLSearchParams(searchParams)
    newSearchParams.set('assistant', assistantName.toLowerCase().replace(/\s+/g, '-'))
    setSearchParams(newSearchParams)
  }

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const handleCloseError = () => {
    setError(null)
  }

  const handleLogout = () => {
    logout()
    navigate('/login')
    setProfileDrawerOpen(false)
  }

  const toggleProfileDrawer = () => {
    setProfileDrawerOpen(!profileDrawerOpen)
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="static">
        <Toolbar sx={{ px: { xs: 1, sm: 2 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <svg
              viewBox="0 0 1000 1000"
              style={{
                height: 40,
                width: 40,
                fill: 'currentColor',
              }}
            >
              <path d="M799.9,400.1H635.5c-19.6,0-35.4,15.8-35.4,35.4v364.4h-200V635.3c0-19.6-15.8-35.4-35.4-35.4H200.1v-200h364.4c19.6,0,35.4-15.8,35.4-35.4V200.1h200V400.1L799.9,400.1z" />
            </svg>
          </Box>
          <AssistantSelector
            selectedAssistant={selectedAssistantId}
            onAssistantChange={handleAssistantChange}
          />
          <IconButton onClick={toggleProfileDrawer} sx={{ ml: 1 }}>
            <ProfileAvatar
              src={user?.picture}
              alt={user?.name}
              sx={{
                width: 32,
                height: 32,
                cursor: 'pointer',
              }}
            />
          </IconButton>
        </Toolbar>
      </AppBar>

      {isLoading && <LinearProgress />}

      <Container
        maxWidth="md"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          py: { xs: 1, sm: 2 },
          px: { xs: 1, sm: 3 },
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            mb: { xs: 1, sm: 2 },
            '&::-webkit-scrollbar': {
              width: { xs: 4, sm: 8 },
            },
            '&::-webkit-scrollbar-track': {
              background: '#f1f1f1',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb': {
              background: '#c1c1c1',
              borderRadius: 4,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: '#a8a8a8',
            },
          }}
        >
          {messages.length === 0 && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
                textAlign: 'center',
              }}
            >
              <Typography variant="h6" color="text.secondary">
                Start a conversation with your Addium AI Assistant
              </Typography>
            </Box>
          )}

          {messages.map((message) => (
            <ChatMessage key={message.id} message={message} />
          ))}
          <div ref={messagesEndRef} />
        </Box>

        <ChatInput onSendMessage={sendMessage} disabled={isLoading || !selectedAssistantId} />
      </Container>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseError} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Profile Drawer */}
      <Drawer
        anchor="right"
        open={profileDrawerOpen}
        onClose={() => setProfileDrawerOpen(false)}
        PaperProps={{
          sx: { width: 320 },
        }}
      >
        <Box sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <ProfileAvatar
              src={user?.picture}
              alt={user?.name}
              sx={{
                width: 64,
                height: 64,
                mr: 2,
              }}
            />
            <Box>
              <Typography variant="h6">{user?.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {user?.email}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {user?.domain}
              </Typography>
            </Box>
          </Box>

          <List>
            <ListItem button onClick={handleLogout}>
              <ListItemIcon>
                <Logout />
              </ListItemIcon>
              <ListItemText primary="Sign out" />
            </ListItem>
          </List>
        </Box>
      </Drawer>
    </Box>
  )
}
